import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Bell, BellRing, Flame, Zap, MessageSquare, Calendar, Briefcase, RefreshCw, CheckCheck, Filter, ChevronDown, TrendingUp } from 'lucide-react';
import {
  fetchAdminNotifications,
  subscribeToAdminEvents,
  listenToGA4Events,
  scoreHotLead,
  ADMIN_NOTIF_TYPES,
} from '../../../services/adminNotificationService';

// ─── Priority Config ──────────────────────────────────────────────────────────

const PRIORITY_CONFIG = {
  critical: {
    label: 'Très Chaud 🔥',
    bg: 'bg-red-50',
    border: 'border-red-300',
    badge: 'bg-red-100 text-red-700 border border-red-200',
    dot: 'bg-red-500',
    icon: Flame,
    iconColor: 'text-red-500',
    ring: 'ring-2 ring-red-200',
  },
  high: {
    label: 'Chaud ⚡',
    bg: 'bg-orange-50',
    border: 'border-orange-200',
    badge: 'bg-orange-100 text-orange-700 border border-orange-200',
    dot: 'bg-orange-400',
    icon: Zap,
    iconColor: 'text-orange-500',
    ring: '',
  },
  medium: {
    label: 'Tiède',
    bg: 'bg-yellow-50',
    border: 'border-yellow-200',
    badge: 'bg-yellow-100 text-yellow-700 border border-yellow-200',
    dot: 'bg-yellow-400',
    icon: Bell,
    iconColor: 'text-yellow-500',
    ring: '',
  },
  low: {
    label: 'Froid',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    badge: 'bg-blue-100 text-blue-700 border border-blue-200',
    dot: 'bg-blue-400',
    icon: Bell,
    iconColor: 'text-blue-400',
    ring: '',
  },
};

const TYPE_ICON = {
  [ADMIN_NOTIF_TYPES?.NEW_INQUIRY]: MessageSquare,
  [ADMIN_NOTIF_TYPES?.APPOINTMENT_BOOKED]: Calendar,
  [ADMIN_NOTIF_TYPES?.NEW_BOOKING]: Briefcase,
  [ADMIN_NOTIF_TYPES?.HOT_LEAD]: Flame,
  [ADMIN_NOTIF_TYPES?.GENERATE_LEAD]: TrendingUp,
  [ADMIN_NOTIF_TYPES?.PROPERTY_ACTION]: Bell,
};

const TYPE_LABEL = {
  [ADMIN_NOTIF_TYPES?.NEW_INQUIRY]: 'Demande',
  [ADMIN_NOTIF_TYPES?.APPOINTMENT_BOOKED]: 'Rendez-vous',
  [ADMIN_NOTIF_TYPES?.NEW_BOOKING]: 'Réservation',
  [ADMIN_NOTIF_TYPES?.HOT_LEAD]: 'Lead Chaud',
  [ADMIN_NOTIF_TYPES?.GENERATE_LEAD]: 'Lead GA4',
  [ADMIN_NOTIF_TYPES?.PROPERTY_ACTION]: 'Propriété',
};

// ─── Relative time ────────────────────────────────────────────────────────────

const relativeTime = (dateStr) => {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr)?.getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'À l\'instant';
  if (mins < 60) return `Il y a ${mins} min`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `Il y a ${hrs}h`;
  const days = Math.floor(hrs / 24);
  return `Il y a ${days}j`;
};

// ─── Single Notification Card ─────────────────────────────────────────────────

const NotifCard = ({ notif, onMarkRead, isNew }) => {
  const pCfg = PRIORITY_CONFIG?.[notif?.priority] || PRIORITY_CONFIG?.low;
  const TypeIcon = TYPE_ICON?.[notif?.type] || Bell;
  const PriorityIcon = pCfg?.icon;

  return (
    <div
      className={`relative flex gap-3 p-4 rounded-xl border transition-all duration-300
        ${pCfg?.bg} ${pCfg?.border} ${pCfg?.ring}
        ${notif?.isRead ? 'opacity-70' : ''}
        ${isNew ? 'animate-pulse-once' : ''}
      `}
    >
      {/* Unread dot */}
      {!notif?.isRead && (
        <span className={`absolute top-3 right-3 w-2 h-2 rounded-full ${pCfg?.dot}`} />
      )}
      {/* Type icon */}
      <div className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center bg-white shadow-sm border ${pCfg?.border}`}>
        <TypeIcon className={`w-4 h-4 ${pCfg?.iconColor}`} />
      </div>
      <div className="flex-1 min-w-0">
        {/* Header row */}
        <div className="flex items-start justify-between gap-2 mb-1">
          <p className="text-sm font-semibold text-gray-900 leading-tight truncate">{notif?.title}</p>
          <span className="text-xs text-gray-400 flex-shrink-0">{relativeTime(notif?.createdAt)}</span>
        </div>

        {/* Message */}
        <p className="text-sm text-gray-700 truncate mb-1">{notif?.message}</p>

        {/* Detail */}
        {notif?.detail && (
          <p className="text-xs text-gray-500 truncate mb-2">{notif?.detail}</p>
        )}

        {/* Badges row */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Priority badge */}
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${pCfg?.badge}`}>
            <PriorityIcon className="w-3 h-3" />
            {notif?.leadScore?.label || pCfg?.label}
          </span>

          {/* Type badge */}
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-white border border-gray-200 text-gray-600">
            {TYPE_LABEL?.[notif?.type] || 'Événement'}
          </span>

          {/* GA4 event badge */}
          {notif?.gaEvent && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-50 border border-purple-200 text-purple-600">
              <TrendingUp className="w-3 h-3" />
              GA4
            </span>
          )}

          {/* Price */}
          {notif?.propertyPrice > 0 && (
            <span className="text-xs text-gray-500 ml-auto">
              {new Intl.NumberFormat('fr-FR')?.format(notif?.propertyPrice)} XOF
            </span>
          )}
        </div>
      </div>
      {/* Mark read button */}
      {!notif?.isRead && (
        <button
          onClick={() => onMarkRead?.(notif?.id)}
          className="flex-shrink-0 self-start mt-0.5 p-1 rounded hover:bg-white/60 transition-colors"
          title="Marquer comme lu"
        >
          <CheckCheck className="w-4 h-4 text-gray-400 hover:text-gray-600" />
        </button>
      )}
    </div>
  );
};

// ─── Main Panel ───────────────────────────────────────────────────────────────

const AdminNotificationsPanel = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [readIds, setReadIds] = useState(new Set());
  const [filter, setFilter] = useState('all'); // all | critical | high | inquiry | appointment | booking
  const [showFilter, setShowFilter] = useState(false);
  const [newIds, setNewIds] = useState(new Set());
  const [ga4Events, setGa4Events] = useState([]);
  const unsubscribeRef = useRef(null);
  const ga4CleanupRef = useRef(null);

  // ── Load initial data ──
  const loadNotifications = useCallback(async () => {
    setLoading(true);
    const { data } = await fetchAdminNotifications(60);
    setNotifications(data || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  // ── Real-time subscription ──
  useEffect(() => {
    const unsub = subscribeToAdminEvents((newNotif) => {
      setNotifications((prev) => [newNotif, ...prev]?.slice(0, 100));
      setNewIds((prev) => new Set([...prev, newNotif?.id]));
      // Auto-clear "new" highlight after 5s
      setTimeout(() => {
        setNewIds((prev) => {
          const next = new Set(prev);
          next?.delete(newNotif?.id);
          return next;
        });
      }, 5000);
    });
    unsubscribeRef.current = unsub;
    return () => unsubscribeRef?.current?.();
  }, []);

  // ── GA4 event listener ──
  useEffect(() => {
    const cleanup = listenToGA4Events((eventName, params) => {
      const ga4Notif = {
        id: `ga4-${eventName}-${Date.now()}`,
        sourceId: null,
        type: eventName === 'inquiry_created' ? ADMIN_NOTIF_TYPES?.NEW_INQUIRY
          : eventName === 'appointment_booked' ? ADMIN_NOTIF_TYPES?.APPOINTMENT_BOOKED
          : ADMIN_NOTIF_TYPES?.GENERATE_LEAD,
        priority: eventName === 'generate_lead' || eventName === 'inquiry_created' ? 'high' : 'medium',
        leadScore: scoreHotLead({
          price: params?.property_price || params?.value || 0,
          lead_source: params?.lead_source,
        }),
        title: `GA4: ${eventName?.replace(/_/g, ' ')}`,
        message: params?.event_label || params?.property_id || 'Événement GA4',
        detail: params?.property_commune ? `Commune: ${params?.property_commune}` : '',
        clientName: '',
        clientEmail: '',
        propertyTitle: params?.event_label || '',
        propertyPrice: params?.property_price || params?.value || 0,
        status: 'new',
        createdAt: new Date()?.toISOString(),
        isRead: false,
        actionPath: '/admin-dashboard',
        actionTab: 'funnel',
        gaEvent: eventName,
      };

      setGa4Events((prev) => [ga4Notif, ...prev]?.slice(0, 20));
      setNotifications((prev) => [ga4Notif, ...prev]?.slice(0, 100));
      setNewIds((prev) => new Set([...prev, ga4Notif?.id]));
      setTimeout(() => {
        setNewIds((prev) => {
          const next = new Set(prev);
          next?.delete(ga4Notif?.id);
          return next;
        });
      }, 5000);
    });
    ga4CleanupRef.current = cleanup;
    return () => ga4CleanupRef?.current?.();
  }, []);

  // ── Mark as read ──
  const handleMarkRead = useCallback((id) => {
    setReadIds((prev) => new Set([...prev, id]));
    setNotifications((prev) =>
      prev?.map((n) => (n?.id === id ? { ...n, isRead: true } : n))
    );
  }, []);

  const handleMarkAllRead = useCallback(() => {
    setNotifications((prev) => prev?.map((n) => ({ ...n, isRead: true })));
    setReadIds(new Set(notifications.map((n) => n?.id)));
  }, [notifications]);

  // ── Filtered list ──
  const filtered = notifications?.filter((n) => {
    if (filter === 'all') return true;
    if (filter === 'critical') return n?.priority === 'critical';
    if (filter === 'high') return n?.priority === 'high' || n?.priority === 'critical';
    if (filter === 'inquiry') return n?.type === ADMIN_NOTIF_TYPES?.NEW_INQUIRY;
    if (filter === 'appointment') return n?.type === ADMIN_NOTIF_TYPES?.APPOINTMENT_BOOKED;
    if (filter === 'booking') return n?.type === ADMIN_NOTIF_TYPES?.NEW_BOOKING;
    if (filter === 'ga4') return !!n?.gaEvent;
    return true;
  });

  const unreadCount = notifications?.filter((n) => !n?.isRead)?.length;
  const hotCount = notifications?.filter((n) => n?.priority === 'critical' && !n?.isRead)?.length;

  const FILTERS = [
    { value: 'all', label: 'Toutes' },
    { value: 'critical', label: '🔥 Très Chauds' },
    { value: 'high', label: '⚡ Chauds' },
    { value: 'inquiry', label: 'Demandes' },
    { value: 'appointment', label: 'Rendez-vous' },
    { value: 'booking', label: 'Réservations' },
    { value: 'ga4', label: 'Événements GA4' },
  ];

  return (
    <div className="space-y-6">
      {/* ── Header ── */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-sm">
                <BellRing className="w-5 h-5 text-white" />
              </div>
              {unreadCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Notifications Admin</h2>
              <p className="text-sm text-gray-500">
                Demandes, réservations &amp; leads GA4 en temps réel
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {/* Hot leads badge */}
            {hotCount > 0 && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-100 border border-red-200 text-red-700 text-sm font-semibold animate-pulse">
                <Flame className="w-4 h-4" />
                {hotCount} lead{hotCount > 1 ? 's' : ''} très chaud{hotCount > 1 ? 's' : ''}
              </span>
            )}

            {/* Filter dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowFilter((v) => !v)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-600 hover:bg-gray-50 transition-colors"
              >
                <Filter className="w-4 h-4" />
                {FILTERS?.find((f) => f?.value === filter)?.label || 'Filtrer'}
                <ChevronDown className="w-3 h-3" />
              </button>
              {showFilter && (
                <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-xl shadow-lg z-20 py-1">
                  {FILTERS?.map((f) => (
                    <button
                      key={f?.value}
                      onClick={() => { setFilter(f?.value); setShowFilter(false); }}
                      className={`w-full text-left px-4 py-2 text-sm transition-colors hover:bg-gray-50
                        ${filter === f?.value ? 'text-amber-600 font-semibold bg-amber-50' : 'text-gray-700'}`}
                    >
                      {f?.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Mark all read */}
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-600 hover:bg-gray-50 transition-colors"
              >
                <CheckCheck className="w-4 h-4" />
                Tout lire
              </button>
            )}

            {/* Refresh */}
            <button
              onClick={loadNotifications}
              disabled={loading}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </button>
          </div>
        </div>

        {/* Stats row */}
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Total', value: notifications?.length, color: 'text-gray-700', bg: 'bg-gray-50' },
            { label: 'Non lus', value: unreadCount, color: 'text-blue-700', bg: 'bg-blue-50' },
            { label: 'Leads chauds', value: notifications?.filter((n) => n?.priority === 'critical' || n?.priority === 'high')?.length, color: 'text-orange-700', bg: 'bg-orange-50' },
            { label: 'Événements GA4', value: ga4Events?.length, color: 'text-purple-700', bg: 'bg-purple-50' },
          ]?.map((s) => (
            <div key={s?.label} className={`${s?.bg} rounded-xl p-3 text-center`}>
              <p className={`text-xl font-bold ${s?.color}`}>{s?.value}</p>
              <p className="text-xs text-gray-500 mt-0.5">{s?.label}</p>
            </div>
          ))}
        </div>
      </div>
      {/* ── Notification Feed ── */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="font-semibold text-gray-800 text-sm">
            {filtered?.length} notification{filtered?.length !== 1 ? 's' : ''}
            {filter !== 'all' && ` · ${FILTERS?.find((f) => f?.value === filter)?.label}`}
          </h3>
        </div>

        {loading ? (
          <div className="p-6 space-y-3">
            {[...Array(5)]?.map((_, i) => (
              <div key={i} className="h-20 bg-gray-100 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : filtered?.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center px-6">
            <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center mb-4">
              <Bell className="w-7 h-7 text-gray-400" />
            </div>
            <p className="text-gray-600 font-medium">Aucune notification</p>
            <p className="text-sm text-gray-400 mt-1">
              Les nouvelles demandes et réservations apparaîtront ici en temps réel.
            </p>
          </div>
        ) : (
          <div className="p-4 space-y-3 max-h-[680px] overflow-y-auto">
            {filtered?.map((notif) => (
              <NotifCard
                key={notif?.id}
                notif={notif}
                onMarkRead={handleMarkRead}
                isNew={newIds?.has(notif?.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminNotificationsPanel;
